<script setup>
import useStore from "../store";
import {computed, ref, watch} from "vue";
import {trackEvent} from "../../helpers/track";
import { confirmationValue, normaliseConfirmationValues } from "../helpers/confirmations";
import { normaliseParticipantKey } from "../helpers/format";

const props = defineProps({
  errors: Array,
  submitted: {
    default: false,
    type: Boolean,
  },
})

const store = useStore();
const confirms = ref(normaliseConfirmationValues(store.confirms))
const model = defineModel({ default: false })

const quoteItems = computed(() => store.quote?.items || [])

const sameConfirmationValues = (left = [], right = []) => {
  const normalisedLeft = normaliseConfirmationValues(left)
  const normalisedRight = normaliseConfirmationValues(right)

  return normalisedLeft.length === normalisedRight.length
    && normalisedLeft.every((value) => normalisedRight.includes(value))
}

const participantTypesByAttraction = computed(() => {
  return store.items.reduce((current, item) => {
    if (!item?.attraction || !item?.participants) {
      return current
    }

    const attractionId = item.attraction
    const participantTypes = current[attractionId] ?? []

    Object.entries(item.participants).forEach(([key, count]) => {
      if (Number(count ?? 0) <= 0) {
        return
      }

      participantTypes.push(normaliseParticipantKey(key))
    })

    current[attractionId] = [...new Set(participantTypes)]
    return current
  }, {})
})

const confirmations = computed(() => {
  return store.uniqueAttractions
    .filter((att) => att.confirmations && att.confirmations.length > 0)
    .reduce((current, attraction) => {
      const participantTypes = participantTypesByAttraction.value[attraction.id] ?? []
      const filteredConfirmations = attraction.confirmations.filter((confirmation) => {
        const ticketTypes = Array.isArray(confirmation.ticket_types)
          ? confirmation.ticket_types.map(normaliseParticipantKey)
          : []

        if (!ticketTypes.length || !participantTypes.length) {
          return true
        }

        return ticketTypes.some((value) => participantTypes.includes(value))
      })

      return [...current, ...filteredConfirmations]
    }, [])
})

const requiredConfirmationValues = computed(() => confirmations.value.map((confirmation) => (
  confirmationValue(confirmation.message)
)))

const syncModel = () => {
  model.value = requiredConfirmationValues.value.every((value) => confirms.value.includes(value))
}

const updateSubscribe = (event) => {
  if (!event.target.checked){
    trackEvent('newsletter-un-tick');
  } else {
    trackEvent('newsletter-re-tick');
  }
}

watch(() => store.confirms, (value) => {
  const nextConfirms = normaliseConfirmationValues(value)

  if (!sameConfirmationValues(confirms.value, nextConfirms)) {
    confirms.value = nextConfirms
  }

  syncModel()
}, { deep: true, immediate: true })

watch(confirms, (value) => {
  const nextConfirms = normaliseConfirmationValues(value)

  if (!sameConfirmationValues(value, nextConfirms)) {
    confirms.value = nextConfirms
    return
  }

  if (!sameConfirmationValues(store.confirms, nextConfirms)) {
    store.setConfirms(nextConfirms)
  }

  syncModel()
}, { deep: true })

watch(requiredConfirmationValues, syncModel, { deep: true, immediate: true })

</script>

<template>
    <div>
        <div
          v-for="(confirmation, ind) in confirmations"
          :key="`confirmation-${confirmation.message}-${ind}`"
          class="relative"
        >
          <div class="customer-field py-3">
            <label class="flex items-center">
              <input
                  v-model="confirms"
                  type="checkbox"
                  name="confirm"
                  :value="confirmationValue(confirmation.message)"
                  :id="`confirmation_${ind}`"
                  class="border-alert-error border mr-3 h-6 w-6"
              />
              <span class="flex-grow">{{ confirmation.message }}</span>
            </label>
          </div>
        </div>
        <div v-if="props.submitted && !model"
             class="text-[#F54E4E] text-sm mt-1">
          Please confirm all the requirements for your booking
        </div>

        <div class="relative" v-if="quoteItems.length > 0">
          <div class="customer-field py-3">
            <label class="flex items-center">
              <input
                  v-model="store.subscribe"
                  @change="updateSubscribe"
                  type="checkbox"
                  name="confirm"
                  :id="`confirmation_subscribe`"
                  :true-value="true"
                  :false-value="false"
                  class="border mr-3 h-6 w-6"
              />
              <span class="flex-grow">Subscribe to Capes Foundation for the latest news, events and offers</span>
            </label>
          </div>
        </div>



    </div>
</template>
